import { useEffect, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  PiArrowRight,
  PiFile,
  PiFileImage,
  PiFilePdf,
  PiFileVideo,
  PiMagnifyingGlass,
  PiX,
} from 'react-icons/pi'
import useUiContext from '@/custolink-hooks/useUiContext'
import GetPath from '@/custolink-logic/api/GetPath'
import {
  FrontendFilesApi,
  type FrontendFilesApi$SearchHit,
} from '@/custolink-transpiled_by_server/bc.asset_files.api_methods'
import { FrontendFilesApi as FrontendFilesApiGeneric } from '@/custolink-transpiled_by_server/bc.asset_files.api_generic'
import type { PublicAssetFrontendApi$AssetComponentDto } from '@/custolink-transpiled_by_server/bc.asset_interaction.api_methods'
import type { AssetViewUrlParams } from '@/custolink-views/asset_frontend/asset_view/useAssetViewFacade'
import { componentIcon } from './componentIcon'
import { OrScan, RowGroup } from './tileKit'

const MIN_QUERY = 2

function fileIcon(mediaType: string) {
  if (mediaType === 'application/pdf') return <PiFilePdf />
  if (mediaType.startsWith('image/')) return <PiFileImage />
  if (mediaType.startsWith('video/')) return <PiFileVideo />
  return <PiFile />
}

/**
 * The asset page's search, after the reference: a bordered field and a labelled
 * Scan key. While a query is typed the page content gives way to the results -
 * matching documentation files (by file and folder name, server-side, same
 * access rules as the file lists) and, when the organisation offers AI support,
 * a first row that hands the query to the AI chat as its question.
 */
export default function AssetSearch({
  urlParams,
  components,
  aiSupport,
  onAskAi,
  onScan,
  children,
}: {
  urlParams: AssetViewUrlParams
  components: PublicAssetFrontendApi$AssetComponentDto[]
  aiSupport: PublicAssetFrontendApi$AssetComponentDto | null
  onAskAi: (question: string) => void
  onScan: () => void
  children: ReactNode
}) {
  const { t } = useTranslation()
  const uic = useUiContext()
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const handler = setTimeout(() => setDebounced(query.trim()), 300)
    return () => clearTimeout(handler)
  }, [query])

  const active = query.trim().length >= MIN_QUERY
  const search = useQuery({
    queryKey: ['FrontendFilesApi.searchFiles', uic._orgId, urlParams, debounced],
    // The generated client concatenates the query into the URL, so encode it here.
    queryFn: () =>
      FrontendFilesApi.searchFiles(
        uic._orgId,
        urlParams.viewId,
        urlParams.viewValue,
        urlParams.assetId,
        encodeURIComponent(debounced)
      ),
    enabled: debounced.length >= MIN_QUERY,
  })

  const hrefFor = (fileId: number) =>
    FrontendFilesApiGeneric.downloadFile(
      GetPath,
      uic._orgId,
      urlParams.viewId,
      urlParams.viewValue,
      urlParams.assetId,
      fileId
    )

  const componentName = (hit: FrontendFilesApi$SearchHit) =>
    components.find((c) => c.id === hit.componentId)?.name ?? ''

  const hits = search.data?.hits ?? []
  const settled = !search.isFetching && debounced === query.trim()

  return (
    <>
      <div className="flex items-stretch gap-2">
        <label className="flex min-w-0 flex-1 items-center gap-2 rounded-lg sm:rounded-xl border border-[color:var(--rda-line)] bg-[var(--rda-surface)] px-3 text-[var(--rda-on-surface)] focus-within:border-[color:var(--rda-accent-text)] focus-within:ring-1 focus-within:ring-[color:var(--rda-accent-text)]">
          <PiMagnifyingGlass aria-hidden className="h-5 w-5 shrink-0 text-[var(--rda-muted)]" />
          <input
            type="search"
            value={query}
            placeholder={t('assetView.search.placeholder')}
            aria-label={t('assetView.search.placeholder')}
            className="w-full min-w-0 bg-transparent py-2.5 text-base sm:py-3 outline-none placeholder:text-[color:var(--rda-muted)] [&::-webkit-search-cancel-button]:hidden"
            onChange={(e) => setQuery(e.target.value)}
          />
          {search.isFetching ? (
            <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent opacity-50" />
          ) : query.length > 0 ? (
            <button
              type="button"
              aria-label={t('assetView.search.clear')}
              title={t('assetView.search.clear')}
              className="-me-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--rda-muted)] hover:bg-black/5"
              onClick={() => setQuery('')}
            >
              <PiX className="h-4 w-4" />
            </button>
          ) : null}
        </label>
        <OrScan onScan={onScan} />
      </div>

      {active ? (
        <div aria-live="polite" className="flex flex-col gap-3">
          {aiSupport != null ? (
            <RowGroup>
              <button
                type="button"
                className="rda-tile flex w-full items-center gap-3 bg-[var(--rda-accent-wash)] px-4 py-2.5 text-start sm:py-4 text-[var(--rda-on-surface)]"
                onClick={() => onAskAi(query.trim())}
              >
                <span aria-hidden className="text-[22px] text-[var(--rda-accent-text)] sm:text-[28px]">
                  {componentIcon(aiSupport.key)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium leading-tight tracking-[-0.01em] sm:text-base">
                    {t('assetView.search.askAi')}
                  </span>
                  <span className="rda-tile-muted mt-0.5 block truncate text-[13px] text-[var(--rda-muted)] sm:text-sm">
                    “{query.trim()}”
                  </span>
                </span>
                <PiArrowRight aria-hidden className="h-5 w-5 shrink-0 text-[var(--rda-accent-text)] rtl:-scale-x-100" />
              </button>
            </RowGroup>
          ) : null}

          {hits.length > 0 ? (
            <RowGroup>
              {hits.map((hit) => (
                <a
                  key={`${hit.componentId}-${hit.fileId}`}
                  href={hrefFor(hit.fileId)}
                  target="_blank"
                  rel="noreferrer"
                  className="rda-tile flex w-full items-center gap-3 bg-[var(--rda-surface)] px-4 py-2.5 text-start sm:py-3.5 text-[var(--rda-on-surface)]"
                >
                  <span aria-hidden className="text-[22px] sm:text-2xl">
                    {fileIcon(hit.mediaType)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block break-words text-sm font-medium leading-snug sm:text-[15px]">
                      {hit.filename}
                    </span>
                    <span className="rda-tile-muted mt-0.5 block truncate text-[13px] text-[var(--rda-muted)] sm:text-sm">
                      {[componentName(hit), hit.folderPath.split('/').join(' / ')]
                        .filter((s) => s.length > 0)
                        .join(' · ')}
                    </span>
                  </span>
                </a>
              ))}
            </RowGroup>
          ) : settled && search.isSuccess ? (
            <p className="px-1 text-sm text-[var(--rda-muted)]">{t('assetView.search.noResults')}</p>
          ) : null}
        </div>
      ) : (
        children
      )}
    </>
  )
}
